import { cloneDeep, find, get, isEmpty, isEqual } from 'lodash';
import { connect } from 'react-redux';
import { unstable_batchedUpdates } from 'react-dom';
import i18next from 'i18next';
import React, { useCallback, useEffect, useState } from 'react';

import { openGenericModal, refreshGenericModal } from '@actions/modal';

import { getIngredientUnitName, handleUnitsValidity } from '@commons/utils/units';
import { translatedUnitCondensed } from '@commons/utils/translateUnit';
import WhiteCardForm from '@commons/WhiteCardForm';

import { convertPriceToPackagingById } from '@commons/utils/packagings';

import { convertSPPQuantityInMasterUnit } from '@orders/utils/computePackagingToSupplierProduct';

import {
  DropdownIcon,
  DropdownTitle,
} from '@admin/suppliers/supplierProducts/details/supplierProductInformations/components/SupplierProductDetails/styledComponents';
import { getConversionModalParams } from '@admin/suppliers/supplierProducts/details/supplierProductInformations/components/InputConversionContentModal/utils/modalConfigurations';
import { getRecipeUnit } from '@admin/products/recipes/detail/components/RecipeInfo/common/inputs';
import { UNITS_DROPDOWN_ITEMS } from '@admin/utils/DropdownItems';
import PackagingSection from '@admin/suppliers/supplierProducts/components/PackagingSection';
import QuickCreateIngredientModal from '@admin/components/QuickCreateIngredientModal';

import {
  SUPPLIER_PRODUCT_INGREDIENTS_INPUTS,
  SUPPLIER_PRODUCT_PRICE_INPUTS,
} from '../../utils/formInputsConfigurations';

import {
  Container,
  DropdownItem,
  DropdownItemMainValue,
  DropdownItemSecondaryValue,
} from './styledComponents';

const DEFAULT_INGREDIENT_PICTURE = '/images/inpulse/ingredient-photo-placeholder.svg';

const KitchenProductSupplierProductSection = (props) => {
  const {
    productForm,
    formFields,
    supplierProduct,
    setSupplierProduct,
    ingredients,
    setIngredients,
    composition,
    storeName,
    currency,
    isSaveAlreadyTriggered,
    openGenericModal,
    refreshGenericModal,
  } = props;

  const [packagings, setPackagings] = useState(supplierProduct.packagings || []);
  const [conversions, setConversions] = useState([]);
  const [associatedIngredients, setAssociatedIngredients] = useState(ingredients || []);

  const [isQuickCreateIngredientModalOpen, setIsQuickCreateIngredientModalOpen] = useState(false);
  const [createdIngredientId, setCreatedIngredientId] = useState(null);

  const handleIngredientCreation = useCallback(() => {
    setIsQuickCreateIngredientModalOpen(true);
  }, []);

  const [ingredientsInput, setIngredientsInput] = useState(
    SUPPLIER_PRODUCT_INGREDIENTS_INPUTS({
      ingredients: associatedIngredients,
      packagingUnit: packagings[0] ? packagings[0].unit : null,
      entityId: get(formFields, 'entity.id'),
      conversions,
      composition,
      handleIngredientCreation,
    }),
  );
  const [priceInputs, setPriceInputs] = useState(
    SUPPLIER_PRODUCT_PRICE_INPUTS({
      storeName,
      packagings,
      currency,
    }),
  );

  useEffect(() => {
    const formattedFormFields = {
      ...cloneDeep(formFields),
      packagings,
    };

    const compositionUnit = _getCompositionUnit();

    const entity = productForm.getValues('entity');

    const isEntityExemptFromUnitReset =
      composition.isIngredient || entity?.id === createdIngredientId;

    if (!isEmpty(entity) && !isEntityExemptFromUnitReset) {
      const isEntityUnitValid = handleUnitsValidity(
        compositionUnit,
        entity.unit,
        get(formattedFormFields, 'conversions', []),
        packagings,
      );

      if (!isEntityUnitValid) {
        productForm.setValue('entity', {});
      }
    }

    setConversions(formattedFormFields.conversions || []);
    setSupplierProduct(formattedFormFields);
  }, [formFields, packagings]);

  useEffect(() => {
    setPriceInputs(
      SUPPLIER_PRODUCT_PRICE_INPUTS({
        storeName,
        packagings,
        currency,
      }),
    );
  }, [packagings, currency]);

  /**
   * Reacts to the user picking a different invoice packaging in the price section: converts the
   * price previously expressed on the old invoice packaging to the new one and moves the
   * isUsedInInvoice flag onto it (same behavior as POS supplier products, see
   * SupplierProductDetails).
   */
  useEffect(() => {
    const newInvoicePackaging = productForm.getValues('invoicePackaging');

    if (isEmpty(packagings) || isEmpty(newInvoicePackaging)) {
      return;
    }

    const currentInvoicePackaging = find(packagings, ['isUsedInInvoice', true]);

    if (currentInvoicePackaging?.id === newInvoicePackaging.id) {
      return;
    }

    if (currentInvoicePackaging && formFields.price) {
      const convertedPrice = convertPriceToPackagingById(
        formFields.price,
        currentInvoicePackaging.id,
        newInvoicePackaging.id,
        packagings,
      );

      if (convertedPrice != null && !Number.isNaN(convertedPrice)) {
        productForm.setValue('price', convertedPrice);
      }
    }

    const updatedPackagings = packagings.map((packaging) => ({
      ...packaging,
      isUsedInInvoice: packaging.id === newInvoicePackaging.id,
    }));

    setPackagings(updatedPackagings);
    productForm.setValue('packagings', updatedPackagings);
  }, [productForm.watch('invoicePackaging')]);

  useEffect(() => {
    const compositionUnit = _getCompositionUnit();

    setIngredientsInput(
      SUPPLIER_PRODUCT_INGREDIENTS_INPUTS({
        ingredients: getIngredientsDropdownItems(compositionUnit),
        packagingUnit: packagings[0] ? packagings[0].unit : null,
        entityId: get(formFields, 'entity.id'),
        conversions,
        composition,
        handleConversionCreation,
        handleIngredientCreation,
      }),
    );
  }, [packagings, ingredients, productForm.watch('entity'), conversions]);

  useEffect(() => {
    if (!createdIngredientId) {
      return;
    }

    productForm.trigger('entity');
  }, [formFields]);

  const _formatPackagingUnit = (unit) => (unit !== 'unit' ? getRecipeUnit(unit) : unit);

  const _getCompositionUnit = () => {
    if (!composition) {
      return null;
    }

    return _formatPackagingUnit(composition.unit);
  };

  const handlePackagingsChange = (newPackagings) => {
    /*
      The invoice packaging defaults to the order packaging when packagings are first created;
      once set (even by this default), it must survive further packaging edits, unless the
      packaging holding it was itself removed — then it falls back to the order packaging,
      like POS supplier products.
    */
    const currentInvoicePackagingId = get(productForm.getValues('invoicePackaging'), 'id');
    const keepsCurrentInvoicePackaging =
      !!currentInvoicePackagingId &&
      newPackagings.some(({ id }) => id === currentInvoicePackagingId);

    const formattedNewPackagings = newPackagings.map((packaging) => ({
      ...packaging,
      isUsedInInvoice: keepsCurrentInvoicePackaging
        ? packaging.id === currentInvoicePackagingId
        : packaging.isUsedInOrder,
    }));

    const initialUsedInOrder = find(packagings, ['isUsedInOrder', true]);
    const newUsedInOrder = find(formattedNewPackagings, ['isUsedInOrder', true]);

    const newUnit = !isEmpty(formattedNewPackagings)
      ? get(formattedNewPackagings, '[0].unit')
      : null;

    productForm.setValue('packagingUnit', newUnit);

    // Price conversions are driven by the invoice packaging effect above once one is set; only
    // convert on order packaging change while no invoice packaging is carried over (first
    // packagings creation, or the packaging holding it was removed).
    if (
      !keepsCurrentInvoicePackaging &&
      formFields.price &&
      initialUsedInOrder &&
      newUsedInOrder &&
      !isEqual(initialUsedInOrder, newUsedInOrder)
    ) {
      const unitPrice =
        formFields.price / convertSPPQuantityInMasterUnit(initialUsedInOrder.id, packagings);
      const convertedQuantity = convertSPPQuantityInMasterUnit(newUsedInOrder.id, packagings);

      productForm.setValue('price', unitPrice * convertedQuantity);
    }

    const newInvoicePackaging = formattedNewPackagings.find(
      ({ isUsedInInvoice }) => isUsedInInvoice,
    );

    productForm.setValue('invoicePackaging', newInvoicePackaging || null);
    setPackagings(formattedNewPackagings);
    productForm.setValue('packagings', formattedNewPackagings);
  };

  const _formatIngredientDropdownItem = (ingredient, compositionUnit) => {
    const isUnitValid = handleUnitsValidity(
      compositionUnit,
      ingredient.unit,
      conversions,
      packagings,
    );

    return {
      ...ingredient,
      id: ingredient.id,
      tags: [getIngredientUnitName(ingredient.unit)],
      value: ingredient.name,
      isDisabled: !isUnitValid,
      renderValue: () => (
        <DropdownItem>
          <DropdownIcon src={ingredient.img || DEFAULT_INGREDIENT_PICTURE} />
          <DropdownTitle>
            <DropdownItemMainValue>{ingredient.name}</DropdownItemMainValue>
            <DropdownItemSecondaryValue isDisabled={!isUnitValid}>
              {`${ingredient.supplierProductMappingCount} ${i18next.t(
                'ADMIN.SUPPLIER_PRODUCTS.SUPPLIER_PRODUCT_ABBREVIATION',
              )}`}
            </DropdownItemSecondaryValue>
          </DropdownTitle>
        </DropdownItem>
      ),
    };
  };

  const getIngredientsDropdownItems = (compositionUnit) =>
    ingredients.map((ingredient) => _formatIngredientDropdownItem(ingredient, compositionUnit));

  const handleIngredientCreated = useCallback(
    (createdIngredient) => {
      const compositionUnit = _getCompositionUnit();

      const createdIngredientWithMappingCount = {
        ...createdIngredient,
        supplierProductMappingCount: 0,
      };

      unstable_batchedUpdates(() => {
        setCreatedIngredientId(createdIngredient.id);
        setIngredients((currentIngredients) => [
          ...currentIngredients,
          createdIngredientWithMappingCount,
        ]);
        productForm.setValue(
          'entity',
          _formatIngredientDropdownItem(createdIngredientWithMappingCount, compositionUnit),
        );
      });
    },
    [composition, conversions, packagings],
  );

  const handleQuickCreateIngredientModalClose = useCallback(() => {
    setIsQuickCreateIngredientModalOpen(false);
  }, []);

  const handleConversionsUpdate = (newConversions) => {
    const matchingUnit = UNITS_DROPDOWN_ITEMS().find(
      ({ id }) => id === productForm.getValues('packagingUnit'),
    );

    if (matchingUnit) {
      const conversionModalParams = getConversionModalParams({
        packagingUnit: matchingUnit,
        existingConversions: newConversions,
        areConversionsValid,
        handleConversionsUpdate,
        handleConversionSaving,
      });

      refreshGenericModal(conversionModalParams);
    }
  };

  const handleConversionCreation = (packagingUnit) => {
    const matchingUnit = UNITS_DROPDOWN_ITEMS().find(({ id }) => id === packagingUnit);

    if (matchingUnit) {
      const conversionModalParams = getConversionModalParams({
        packagingUnit: matchingUnit,
        existingConversions: conversions,
        areConversionsValid,
        handleConversionsUpdate,
        handleConversionSaving,
      });

      openGenericModal(conversionModalParams);
    }
  };

  const areConversionsValid = (newConversions, packagingUnit) => {
    const packagingConversion = newConversions.find(
      ({ convertedUnit }) => convertedUnit === packagingUnit.id,
    );

    if (!packagingConversion) {
      return false;
    }

    const allConversionsAreNull = newConversions.every(
      ({ convertedQuantity }) => convertedQuantity === '',
    );

    const isOneConversionEqualToZero = newConversions.some(
      ({ convertedQuantity }) => convertedQuantity === '0',
    );

    return (
      !isOneConversionEqualToZero &&
      (allConversionsAreNull || packagingConversion.convertedQuantity !== '')
    );
  };

  const handleConversionSaving = (newConversions) => {
    const hasNoQuantities = (conversions) =>
      conversions.every(({ convertedQuantity }) => convertedQuantity === '');

    const allConversionsHaveNoQuantities = hasNoQuantities(newConversions);

    const conversionOtherThanBaseUnit = newConversions.filter(({ isBaseUnit }) => !isBaseUnit);
    const onlyPackagingHaveQuantity =
      conversionOtherThanBaseUnit.length >= 1 && hasNoQuantities(conversionOtherThanBaseUnit);

    const compositionUnit = _getCompositionUnit();

    if (allConversionsHaveNoQuantities || onlyPackagingHaveQuantity) {
      _resetConversions();

      const ingredientsDropdownItems = getIngredientsDropdownItems(compositionUnit, conversions);

      setAssociatedIngredients(ingredientsDropdownItems);
      return;
    }

    const conversionWithValues = newConversions.filter(
      ({ convertedUnit, convertedQuantity }) => convertedUnit && convertedQuantity,
    );

    const conversionsDisplayed = _getConversionsDisplay(conversionWithValues);

    productForm.setValue('conversionsDisplayed', conversionsDisplayed);
    productForm.setValue('conversions', conversionWithValues);

    setConversions(conversionWithValues);

    const ingredientsDropdownItems = getIngredientsDropdownItems(
      compositionUnit,
      ingredients,
      newConversions,
    );

    setAssociatedIngredients(ingredientsDropdownItems);
  };

  const _getConversionsDisplay = (conversions) =>
    conversions
      .map(
        ({ convertedQuantity, convertedUnit }) =>
          `${convertedQuantity} ${translatedUnitCondensed(convertedUnit)}`,
      )
      .join(' = ');

  const _resetConversions = () => {
    productForm.setValue('conversionsDisplayed', '');
    productForm.setValue('conversions', []);
    setConversions([]);
  };

  return (
    <Container>
      <PackagingSection
        handleSupplierProductChange={handlePackagingsChange}
        hasRightToEditPackagings={true}
        inRecipe={true}
        isErrorState={isEmpty(packagings) && isSaveAlreadyTriggered}
        supplierProduct={{ ...formFields, packagings }}
      />
      <WhiteCardForm
        form={productForm}
        inputs={ingredientsInput}
        shouldDisplayError={isSaveAlreadyTriggered || !!createdIngredientId}
        title={i18next.t('GENERAL.ASSOCIATED_INGREDIENT')}
      />
      <WhiteCardForm
        form={productForm}
        inputs={priceInputs}
        shouldDisplayError={isSaveAlreadyTriggered}
        title={i18next.t('GENERAL.PRICE')}
      />
      {isQuickCreateIngredientModalOpen && (
        <QuickCreateIngredientModal
          onClose={handleQuickCreateIngredientModalClose}
          onIngredientCreated={handleIngredientCreated}
        />
      )}
    </Container>
  );
};

const mapDispatchToProps = (dispatch) => ({
  openGenericModal: (params) => {
    dispatch(openGenericModal(params));
  },
  refreshGenericModal: (params, customPropertyPathsToUpdate = []) =>
    dispatch(refreshGenericModal(params, customPropertyPathsToUpdate)),
});

export default connect(null, mapDispatchToProps)(KitchenProductSupplierProductSection);
